import { useEffect, useState } from 'react';
import { FiBarChart2, FiCheckCircle, FiInbox } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { enquetesService, type Enquete, type ResultadoEnquete } from '../../services/enquetes.service';

export function EnquetesPage() {
  const { cidadao } = useAuth();
  const [enquetes, setEnquetes] = useState<Enquete[]>([]);
  const [votos, setVotos] = useState<Record<string, string>>({});
  const [resultados, setResultados] = useState<Record<string, ResultadoEnquete>>({});
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!cidadao) return;
    async function carregar() {
      const [lista, meus] = await Promise.all([
        enquetesService.listarAtivas(getEstabelecimentoAtualId()),
        enquetesService.meusVotos(cidadao!.id),
      ]);
      setEnquetes(lista);
      setVotos(meus);
      // resultados das que já votou
      const jaVotadas = lista.filter((e) => meus[e.id]);
      const res = await Promise.all(jaVotadas.map((e) => enquetesService.resultados(e.id).then((r) => [e.id, r] as const)));
      setResultados(Object.fromEntries(res));
      setCarregando(false);
    }
    carregar();
  }, [cidadao]);

  async function votar(enquete: Enquete, opcaoId: string) {
    if (!cidadao) return;
    await enquetesService.votar(enquete.id, cidadao.id, opcaoId);
    setVotos((prev) => ({ ...prev, [enquete.id]: opcaoId }));
    const r = await enquetesService.resultados(enquete.id);
    setResultados((prev) => ({ ...prev, [enquete.id]: r }));
  }

  return (
    <PortalPage
      titulo="Enquetes"
      subtitulo="Participe das consultas da prefeitura. Seu voto é anônimo nos resultados."
      icone={<FiBarChart2 size={26} />}
      voltarHref="/"
      largura="larga"
    >
      <div className="space-y-5">
        {carregando ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : enquetes.length === 0 ? (
          <SectionCard>
            <div className="py-12 text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100"><FiInbox className="text-slate-400" size={26} /></div>
              <p className="text-sm font-medium text-slate-700">Nenhuma enquete ativa no momento</p>
              <p className="mt-1 text-xs text-slate-500">Quando a prefeitura abrir uma consulta, ela aparece aqui.</p>
            </div>
          </SectionCard>
        ) : (
          enquetes.map((e) => (
            <SectionCard key={e.id} titulo={e.pergunta} subtitulo={e.descricao || undefined} icone={<FiBarChart2 size={18} />}>
              {votos[e.id] ? (
                <Resultado enquete={e} resultado={resultados[e.id]} meuVoto={votos[e.id]} />
              ) : (
                <Votacao enquete={e} onVotar={(opcaoId) => votar(e, opcaoId)} />
              )}
            </SectionCard>
          ))
        )}
      </div>
    </PortalPage>
  );
}

function Votacao({ enquete, onVotar }: { enquete: Enquete; onVotar: (opcaoId: string) => Promise<void> }) {
  const [escolha, setEscolha] = useState('');
  const [enviando, setEnviando] = useState(false);

  return (
    <div className="space-y-2">
      {enquete.opcoes.map((o) => (
        <label key={o.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm transition ${escolha === o.id ? 'border-institutional bg-institutional/5' : 'border-slate-200 hover:bg-slate-50'}`}>
          <input type="radio" name={`enq-${enquete.id}`} checked={escolha === o.id} onChange={() => setEscolha(o.id)} className="accent-institutional" />
          <span className="text-slate-800">{o.texto}</span>
        </label>
      ))}
      <Button
        disabled={!escolha}
        loading={enviando}
        onClick={async () => { setEnviando(true); try { await onVotar(escolha); } finally { setEnviando(false); } }}
      >
        Votar
      </Button>
    </div>
  );
}

function Resultado({ enquete, resultado, meuVoto }: { enquete: Enquete; resultado?: ResultadoEnquete; meuVoto: string }) {
  const total = resultado?.total ?? 0;
  return (
    <div className="space-y-2">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-emerald-700">
        <FiCheckCircle size={13} /> Você já votou nesta enquete.
      </div>
      {enquete.opcoes.map((o) => {
        const n = resultado?.por_opcao?.[o.id] ?? 0;
        const pct = total > 0 ? Math.round((n / total) * 100) : 0;
        const meu = o.id === meuVoto;
        return (
          <div key={o.id}>
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className={meu ? 'font-semibold text-institutional' : 'text-slate-700'}>{o.texto}{meu ? ' ✓' : ''}</span>
              <span className="font-semibold tabular-nums text-slate-600">{pct}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full rounded-full ${meu ? 'bg-institutional' : 'bg-slate-400'}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
      <p className="text-xs text-slate-400">{total} voto(s) no total</p>
    </div>
  );
}
